import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Copy, Check, Mail, User, Zap, Banknote, Search, PencilLine, MapPin } from 'lucide-react';
import { UnidadeConsumidora } from '../../types';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import UCSearchPicker, { composeEnderecoUC } from './UCSearchPicker';
import SignatariosSelector, { signatariosVazio } from '../../src/components/SignatariosSelector';
import { SignatariosEscolhidos } from '../../src/lib/termoSignatarios';
import { normalizarCpfCnpj } from '../../src/lib/cobrancaCliente';
import { RegeracaoDocumento, doPayload } from './regeracaoDocumento';
import ConfirmModal from '../../src/components/ConfirmModal';
import { useFechamentoSeguro } from '../../src/hooks/useFechamentoSeguro';
import { useCompletarDadosUc } from '../../src/hooks/useCompletarDadosUc';
import { chavesFaturasUc, montarDadosDocumentoUc, seVazio, type DadosDocumentoUc, type FaturaEmAberto } from '../../src/lib/dadosDocumentoUc';
import { buscarFaturasEmAberto } from '../../src/services/dadosDocumentoUc';

interface NotificacaoMoraModalProps {
    isOpen: boolean;
    onClose: () => void;
    unidade?: UnidadeConsumidora | null;
    /** Preenche o formulário com a emissão anterior, quando veio de "Corrigir e gerar novamente". */
    regeracao?: RegeracaoDocumento | null;
}

const inputCls = "w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100";
const labelCls = "block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";
const sectionTitleCls = "text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 flex items-center gap-2";

const NotificacaoMoraModal: React.FC<NotificacaoMoraModalProps> = ({ isOpen, onClose, unidade, regeracao }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [successData, setSuccessData] = useState<{ link: string; numero: string } | null>(null);
    const [copied, setCopied] = useState(false);
    // Vira true no primeiro toque do operador (digitar, marcar fatura, escolher UC).
    // O preenchimento automatico pela UC nao conta: nao ha trabalho a perder ainda.
    const [rascunho, setRascunho] = useState(false);

    // Modo de preenchimento
    const [modo, setModo] = useState<'buscar' | 'manual'>('buscar');
    const [ucsSelecionadas, setUcsSelecionadas] = useState<UnidadeConsumidora[]>([]);

    // Cliente
    const [nome, setNome] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ'>('PF');

    const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '');
        if (tipoCliente === 'PF') {
            if (val.length > 11) val = val.slice(0, 11);
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            if (val.length > 14) val = val.slice(0, 14);
            val = val.replace(/^(\d{2})(\d)/, '$1.$2');
            val = val.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
            val = val.replace(/\.(\d{3})(\d)/, '.$1/$2');
            val = val.replace(/(\d{4})(\d)/, '$1-$2');
        }
        setCpfCnpj(val);
    };

    // Contato
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');

    // Representante legal da EMPRESA notificada (obrigatório p/ PJ — pedido da Dhaiane)
    const [repNotificadoNome, setRepNotificadoNome] = useState('');
    const [repNotificadoCpf, setRepNotificadoCpf] = useState('');

    // Endereço do notificado (vai ao documento)
    const [endereco, setEndereco] = useState('');

    // Representante legal da Vigor que assina a notificação (default: Config → Assinaturas do Termo)
    const [signatarios, setSignatarios] = useState<SignatariosEscolhidos>(signatariosVazio);

    // UC vinculada (para o log/histórico)
    const [numeroUc, setNumeroUc] = useState('');
    const [ucId, setUcId] = useState<number | null>(null);

    // Débito
    const [vencimentos, setVencimentos] = useState('');
    const [valorTotal, setValorTotal] = useState('');

    // Faturas em aberto da UC — congeladas no log para a verificação do prazo de 5 dias úteis
    const [faturasAbertas, setFaturasAbertas] = useState<FaturaEmAberto[]>([]);
    const [faturasSelecionadas, setFaturasSelecionadas] = useState<number[]>([]);
    const [loadingFaturas, setLoadingFaturas] = useState(false);

    // Data de emissão
    const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().split('T')[0]);

    // Observações internas (não vão ao documento)
    const [obs, setObs] = useState('');

    const formatBRL = (v: number) =>
        v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatVenc = (d: string | null | undefined) => {
        if (!d) return '';
        const [ano, mes, dia] = String(d).split('-');
        return dia && mes && ano ? `${dia}/${mes}/${ano}` : String(d);
    };

    // Carrega as faturas em aberto/vencidas da UC, pelos dois números dela — ver
    // chavesFaturasUc. Status e valor saem da regra única (statusBoleto.ts).
    // `preSelecionar` vem da regeração: sem ela, o padrão é marcar só as vencidas.
    const carregarFaturasAbertas = async (numero: string, outroNumero?: string | null, preSelecionar?: number[]) => {
        const chaves = chavesFaturasUc(numero, outroNumero);
        if (chaves.length === 0) {
            setFaturasAbertas([]);
            setFaturasSelecionadas([]);
            return;
        }

        setLoadingFaturas(true);
        try {
            const faturas = await buscarFaturasEmAberto(chaves);
            setFaturasAbertas(faturas);
            if (preSelecionar?.length) {
                // Regeração: mantém exatamente as faturas da emissão anterior, desde que
                // ainda estejam em aberto (uma quitada no meio do caminho sai sozinha).
                const disponiveis = new Set(faturas.map(f => f.id));
                setFaturasSelecionadas(preSelecionar.filter(id => disponiveis.has(id)));
            } else {
                // Pré-seleciona só as vencidas — que são as que fundamentam a mora
                setFaturasSelecionadas(faturas.filter(f => f.status === 'Vencido').map(f => f.id));
            }
        } catch (err) {
            console.error('Erro ao carregar faturas da UC:', err);
            setFaturasAbertas([]);
            setFaturasSelecionadas([]);
        } finally {
            setLoadingFaturas(false);
        }
    };

    // Vencimentos e valor total acompanham a seleção (o usuário ainda pode editar à mão)
    useEffect(() => {
        if (faturasSelecionadas.length === 0) return;

        const escolhidas = faturasAbertas.filter(f => faturasSelecionadas.includes(f.id));
        if (escolhidas.length === 0) return;

        setVencimentos(escolhidas.map(f => formatVenc(f.vencimento_boleto)).filter(Boolean).join(', '));
        setValorTotal(formatBRL(escolhidas.reduce((s, f) => s + f.valor, 0)));
    }, [faturasSelecionadas, faturasAbertas]);

    const toggleFatura = (id: number) => {
        setFaturasSelecionadas(prev =>
            prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
        );
    };

    const preencherDoCliente = (uc: UnidadeConsumidora) => {
        // Nome e CPF/CNPJ do mesmo cliente — ver montarDadosDocumentoUc.
        const d = montarDadosDocumentoUc(uc);
        setNome(d.nome);
        setCpfCnpj(d.cpfCnpj);
        setTipoCliente(d.tipoCliente);
        setEmail(d.email);
        setWhatsapp(d.whatsapp);
        setEndereco(composeEnderecoUC(uc));
    };

    // O que o cadastro da UC não tem vem da negociação e da última fatura,
    // e só entra em campo vazio. O representante legal da empresa só existe lá.
    const { completar, esquecer } = useCompletarDadosUc();
    const aplicarComplemento = (uc: UnidadeConsumidora, d: DadosDocumentoUc) => {
        setNome(seVazio(d.nome));
        // A UC não tinha CPF/CNPJ e a negociação tem: o tipo acompanha o documento.
        if (!montarDadosDocumentoUc(uc).cpfCnpj && d.cpfCnpj) {
            setCpfCnpj(seVazio(d.cpfCnpj));
            setTipoCliente(d.tipoCliente);
        }
        setEmail(seVazio(d.email));
        setWhatsapp(seVazio(d.whatsapp));
        setEndereco(seVazio(d.enderecoTexto, composeEnderecoUC(uc)));
        setRepNotificadoNome(seVazio(d.representanteNome));
        setRepNotificadoCpf(seVazio(d.representanteCpf));
    };

    useEffect(() => {
        if (isOpen) {
            setSuccessData(null);
            setRascunho(false);
            setObs('');
            setVencimentos('');
            setValorTotal('');
            setDataEmissao(new Date().toISOString().split('T')[0]);
            setSignatarios(signatariosVazio);
            setRepNotificadoNome('');
            setRepNotificadoCpf('');
            setFaturasAbertas([]);
            setFaturasSelecionadas([]);

            if (unidade) {
                setModo('buscar');
                setUcsSelecionadas([unidade]);
                preencherDoCliente(unidade);
                setUcId(unidade.id ?? null);
                setNumeroUc(unidade.numero_uc || '');
                carregarFaturasAbertas(unidade.numero_uc || '', unidade.outro_numero_uc);
                completar(unidade, d => aplicarComplemento(unidade, d));
            } else {
                esquecer();
                setModo('buscar');
                setUcsSelecionadas([]);
                setNome('');
                setCpfCnpj('');
                setTipoCliente('PF');
                setEmail('');
                setWhatsapp('');
                setEndereco('');
                setUcId(null);
                setNumeroUc('');
            }

            // Regeração ("Corrigir e gerar novamente"): o que o operador digitou na
            // emissão cancelada vale mais que o preenchimento automático pela UC —
            // é justamente o que ele quer revisar. Só sobrescreve o que existe no
            // payload; o resto continua vindo da UC.
            const p = regeracao?.payload;
            if (p) {
                const nomeAnterior = doPayload(p, 'cliente_nome');
                const docAnterior = doPayload(p, 'cpf_cnpj');
                if (nomeAnterior) setNome(nomeAnterior);
                if (docAnterior) {
                    setCpfCnpj(docAnterior);
                    setTipoCliente(docAnterior.replace(/\D/g, '').length === 14 ? 'PJ' : 'PF');
                }
                if (p.tipo_cliente === 'PF' || p.tipo_cliente === 'PJ') setTipoCliente(p.tipo_cliente);
                if (doPayload(p, 'email')) setEmail(doPayload(p, 'email'));
                if (doPayload(p, 'whatsapp')) setWhatsapp(doPayload(p, 'whatsapp'));
                if (doPayload(p, 'endereco')) setEndereco(doPayload(p, 'endereco'));
                setRepNotificadoNome(doPayload(p, 'representante_notificado_nome'));
                setRepNotificadoCpf(doPayload(p, 'representante_notificado_cpf'));
                setVencimentos(doPayload(p, 'vencimentos'));
                setValorTotal(doPayload(p, 'valor_total'));
                setObs(doPayload(p, 'obs'));
                // Data de emissão NÃO volta: o documento novo é de hoje.
                const faturasAnteriores = Array.isArray(p.fatura_ids) ? p.fatura_ids as number[] : [];
                const numeroAnterior = doPayload(p, 'numero_uc');
                if (numeroAnterior) {
                    setNumeroUc(numeroAnterior);
                    carregarFaturasAbertas(numeroAnterior, unidade?.outro_numero_uc, faturasAnteriores);
                } else if (faturasAnteriores.length && unidade) {
                    carregarFaturasAbertas(unidade.numero_uc || '', unidade.outro_numero_uc, faturasAnteriores);
                }
            }
        }
    }, [isOpen, unidade, regeracao]);

    const handleClose = () => {
        setSuccessData(null);
        onClose();
    };

    // Depois de gerado nao ha mais o que perder — o sucesso fecha sem perguntar.
    const { propsDoFundo, pedirParaFechar, confirmandoDescarte, descartarEFechar, continuarPreenchendo } =
        useFechamentoSeguro({ isOpen, temRascunho: rascunho && !successData, onFechar: handleClose });

    if (!isOpen) return null;

    const handleAddUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        setUcsSelecionadas([uc]);
        setUcId(uc.id ?? null);
        setNumeroUc(uc.numero_uc || '');
        preencherDoCliente(uc);
        // Trocou de UC: o representante era da empresa anterior.
        setRepNotificadoNome('');
        setRepNotificadoCpf('');
        carregarFaturasAbertas(uc.numero_uc || '', uc.outro_numero_uc);
        completar(uc, d => aplicarComplemento(uc, d));
    };

    const handleRemoveUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        esquecer();
        setUcsSelecionadas(prev => prev.filter(s => s.id !== uc.id));
        setUcId(null);
        setNumeroUc('');
        setFaturasAbertas([]);
        setFaturasSelecionadas([]);
    };

    const handleGenerateNotificacao = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!nome || !cpfCnpj || !email || !whatsapp) {
            addToast('Preencha os campos obrigatórios (Nome, CPF/CNPJ, E-mail e WhatsApp).', 'warning');
            return;
        }
        if (!vencimentos.trim() || !valorTotal.trim()) {
            addToast('Informe o(s) vencimento(s) da(s) fatura(s) e o valor total do débito.', 'warning');
            return;
        }
        if (tipoCliente === 'PJ' && (!repNotificadoNome.trim() || !repNotificadoCpf.trim())) {
            addToast('Para empresas, informe o nome e o CPF do representante legal do notificado.', 'warning');
            return;
        }
        if (!signatarios.procurador) {
            addToast('Selecione o representante legal que assinará a notificação.', 'warning');
            return;
        }

        setLoading(true);

        try {
            const { data: settings } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'notificacao_mora_webhook_url')
                .single();

            const webhookUrl = settings?.value || '';

            if (!webhookUrl) {
                addToast('URL do Webhook de Notificação de Mora não configurada em Config > APIs.', 'error');
                setLoading(false);
                return;
            }

            const payload = {
                id: ucId,
                uc_ids: ucId ? [ucId] : [],
                cliente_nome: nome,
                cpf_cnpj: cpfCnpj,
                tipo_cliente: tipoCliente,
                email: email,
                whatsapp: whatsapp,
                endereco: endereco,
                numero_uc: numeroUc,
                vencimentos: vencimentos,
                valor_total: valorTotal,
                data_emissao: dataEmissao,
                obs: obs,
                // Representante legal da empresa notificada (só PJ — sai no documento)
                representante_notificado_nome: tipoCliente === 'PJ' ? repNotificadoNome : '',
                representante_notificado_cpf: tipoCliente === 'PJ' ? repNotificadoCpf : '',
                // Único signatário do documento — o cliente apenas recebe o PDF assinado
                procurador: signatarios.procurador
            };

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseText = await response.text();
                let n8nResponse: any = null;
                if (responseText) {
                    try {
                        n8nResponse = JSON.parse(responseText);
                    } catch {
                        console.warn('Resposta do webhook não é JSON:', responseText);
                    }
                }

                if (n8nResponse && n8nResponse.link_assinatura_cliente) {
                    setSuccessData({
                        link: n8nResponse.link_assinatura_cliente,
                        numero: n8nResponse.numero_termo || ''
                    });
                    logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'gerar_notificacao_mora', origem: 'acao' });

                    try {
                        await supabase.from('notificacao_mora_logs').insert({
                            uc_id: ucId,
                            uc_numero: numeroUc,
                            cliente_nome: nome,
                            // CPF/CNPJ normalizado do notificado — permite achar esta notificação
                            // por CPF/CNPJ na fila D+17 mesmo que o cadastro da UC mude depois, ou
                            // quando o documento foi gerado sem UC vinculada (modo manual).
                            cpf_cnpj_cliente: normalizarCpfCnpj(cpfCnpj),
                            // Link de acesso ao documento no Autentique (onde o representante legal assina).
                            // O PDF assinado (.../assinado.pdf) só existe DEPOIS que todos assinam — é montado
                            // sob demanda a partir de id_autentique (ver linkPdfAssinado em TermCancelHistoryModal.tsx),
                            // nunca gravado aqui.
                            url_pdf: n8nResponse.url_pdf || n8nResponse.link_assinatura_cliente,
                            numero_termo: n8nResponse.numero_termo || null,
                            user_id: user?.id || null,
                            tipo_cliente: tipoCliente,
                            // Contato do cliente — usado pelo fluxo "Autentique assinado → WhatsApp"
                            whatsapp_cliente: whatsapp,
                            email_cliente: email,
                            valor_total: valorTotal,
                            id_autentique: n8nResponse.id_autentique || null,
                            // Congela o débito cobrado: é o que a verificação de 5 dias úteis confere
                            fatura_ids: faturasSelecionadas.length > 0 ? faturasSelecionadas : null,
                            // Snapshot do formulário. O Autentique não deixa editar um documento
                            // criado, então "corrigir" é cancelar + emitir de novo — e é daqui
                            // que o modal renasce preenchido. `fatura_ids` entra junto porque não
                            // faz parte do payload do n8n mas faz parte do que foi escolhido.
                            payload_geracao: { ...payload, fatura_ids: faturasSelecionadas },
                            // Quando esta emissão é a correção de uma anterior, guarda o elo.
                            substitui_log_id: regeracao?.substituiLogId ?? null
                        });
                    } catch (logError) {
                        console.error('Error logging notificação extrajudicial:', logError);
                    }

                    addToast('Notificação extrajudicial gerada com sucesso!', 'success');
                } else {
                    throw new Error('Retorno inválido do webhook — Link não encontrado na resposta.');
                }
            } else {
                throw new Error(`Erro ${response.status}: ${response.statusText}`);
            }

        } catch (error: any) {
            console.error('Erro ao gerar notificação de mora:', error);
            addToast('Erro ao gerar notificação: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        if (successData?.link) {
            navigator.clipboard.writeText(successData.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return createPortal(
        <>
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsDoFundo}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-500 rounded-lg border border-rose-100 dark:border-rose-800/50">
                            <Banknote size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Gerar Notificação Extrajudicial</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Cobrança e constituição em mora por faturas em atraso.</p>
                        </div>
                    </div>
                    <button
                        onClick={pedirParaFechar}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {successData ? (
                        <div className="flex flex-col items-center justify-center text-center space-y-4 py-6">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mb-2">
                                <Check size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">Notificação Gerada com Sucesso!</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm">
                                O documento aguarda a assinatura do representante legal no Autentique.
                                Assim que ele assinar, o cliente recebe o PDF assinado por WhatsApp automaticamente.
                            </p>
                            <p className="text-xs text-gray-400 dark:text-slate-500 max-w-sm">
                                O link abaixo é o de <strong>assinatura do representante</strong> — use-o para cobrar a assinatura interna.
                            </p>
                            {successData.numero && (
                                <p className="text-xs text-gray-400 dark:text-slate-500">
                                    N° do documento: <span className="font-mono font-semibold">{successData.numero}</span>
                                </p>
                            )}

                            <div className="w-full bg-gray-50 dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 mt-4 flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-slate-300 truncate mr-4">{successData.link}</span>
                                <button
                                    onClick={handleCopyLink}
                                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                                >
                                    {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                                    {copied ? 'Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form id="mora-form" onSubmit={handleGenerateNotificacao} onChange={() => setRascunho(true)} className="space-y-6">

                            {/* Aviso sobre o documento */}
                            <div className="bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800/50 rounded-xl p-4 text-sm text-rose-800 dark:text-rose-300">
                                Constitui o associado em <strong>mora</strong> pelas faturas em atraso, com prazo de <strong>5 dias úteis</strong> para quitação, sob pena de SPC/Serasa, protesto e cobrança judicial.
                            </div>

                            {/* Modo de preenchimento */}
                            <div className="space-y-3">
                                <h3 className={sectionTitleCls}>
                                    <Search size={16} className="text-primary" /> Origem dos Dados
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setModo('buscar')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'buscar'
                                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <Search size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'buscar' ? 'text-primary' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Buscar UC Existente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Preencher a partir de uma UC cadastrada.</p>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setModo('manual')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'manual'
                                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <PencilLine size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'manual' ? 'text-primary' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Preencher Manualmente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Digitar todas as informações da notificação.</p>
                                        </div>
                                    </button>
                                </div>
                                {modo === 'buscar' && (
                                    <UCSearchPicker
                                        selecionadas={ucsSelecionadas}
                                        onAdd={handleAddUcSelecionada}
                                        onRemove={handleRemoveUcSelecionada}
                                        max={1}
                                        placeholder="Buscar UC por cliente, nº da UC ou CPF/CNPJ..."
                                    />
                                )}
                            </div>

                            {/* Cliente Section */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <User size={16} className="text-primary" /> Dados do Notificado
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className={labelCls}>Nome Completo / Razão Social *</label>
                                        <input type="text" required value={nome} onChange={e => setNome(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Tipo *</label>
                                        <select value={tipoCliente} onChange={e => {
                                            setTipoCliente(e.target.value as 'PF' | 'PJ');
                                            setCpfCnpj('');
                                        }} className={inputCls}>
                                            <option value="PF">Pessoa Física</option>
                                            <option value="PJ">Pessoa Jurídica</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>CPF / CNPJ *</label>
                                        <input
                                            type="text"
                                            required
                                            value={cpfCnpj}
                                            onChange={handleCpfCnpjChange}
                                            className={inputCls}
                                            placeholder={tipoCliente === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                                        />
                                    </div>
                                    {tipoCliente === 'PJ' && (
                                        <>
                                            <div>
                                                <label className={labelCls}>Representante Legal da Empresa *</label>
                                                <input
                                                    type="text"
                                                    value={repNotificadoNome}
                                                    onChange={e => setRepNotificadoNome(e.target.value)}
                                                    placeholder="Nome que consta no contrato"
                                                    className={inputCls}
                                                />
                                            </div>
                                            <div>
                                                <label className={labelCls}>CPF do Representante Legal *</label>
                                                <input
                                                    type="text"
                                                    value={repNotificadoCpf}
                                                    onChange={e => setRepNotificadoCpf(e.target.value)}
                                                    placeholder="000.000.000-00"
                                                    className={inputCls}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Contato Section */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <Mail size={16} className="text-primary" /> Contato do Notificado (recebe o documento)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>E-mail *</label>
                                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>WhatsApp *</label>
                                        <input type="text" required value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* Endereço + UC */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <MapPin size={16} className="text-primary" /> Endereço e UC Vinculada
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className={labelCls}>Endereço Completo (consta no documento)</label>
                                        <input type="text" value={endereco} onChange={e => setEndereco(e.target.value)} placeholder="Rua, número, bairro, cidade/UF" className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Número da UC (para o histórico)</label>
                                        <input type="text" value={numeroUc} onChange={e => setNumeroUc(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Data de Emissão *</label>
                                        <input
                                            type="date"
                                            required
                                            value={dataEmissao}
                                            onChange={e => setDataEmissao(e.target.value)}
                                            className={inputCls}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Débito */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <Zap size={16} className="text-rose-500" /> Débito em Aberto
                                </h3>

                                {/* Faturas da UC — marcar aqui é o que permite ao sistema conferir
                                    sozinho, ao fim dos 5 dias úteis, se o associado pagou. */}
                                {loadingFaturas ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400 py-3">
                                        <Loader2 size={14} className="animate-spin" /> Buscando faturas da UC...
                                    </div>
                                ) : faturasAbertas.length > 0 ? (
                                    <div className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
                                        <div className="px-4 py-2.5 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700">
                                            <p className="text-xs font-bold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                                                Faturas cobradas ({faturasSelecionadas.length}/{faturasAbertas.length})
                                            </p>
                                            <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
                                                Ao fim dos 5 dias úteis o sistema confere estas faturas e avisa você se houve pagamento.
                                            </p>
                                        </div>
                                        <div className="max-h-52 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700/50">
                                            {faturasAbertas.map(f => (
                                                <label
                                                    key={f.id}
                                                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={faturasSelecionadas.includes(f.id)}
                                                        onChange={() => toggleFatura(f.id)}
                                                        className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-800 dark:text-slate-100 truncate">
                                                            {f.mes_referencia || 'Sem referência'}
                                                            <span className="text-xs font-normal text-gray-400 dark:text-slate-500 ml-2">UC {f.uc_codigo}</span>
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-slate-400">
                                                            Venc. {formatVenc(f.vencimento_boleto) || '-'}
                                                        </p>
                                                    </div>
                                                    <span className="text-sm font-bold text-gray-700 dark:text-slate-200 flex-shrink-0">
                                                        R$ {formatBRL(f.valor)}
                                                    </span>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border flex-shrink-0 ${
                                                        f.status === 'Vencido'
                                                            ? 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800/50'
                                                            : 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50'
                                                    }`}>
                                                        {f.status}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ) : ucsSelecionadas.length > 0 ? (
                                    <div className="text-xs text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3">
                                        Nenhuma fatura em aberto encontrada para esta UC. Preencha o débito manualmente —
                                        a conferência automática dos 5 dias úteis não será possível.
                                    </div>
                                ) : null}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>Vencimento(s) da(s) Fatura(s) *</label>
                                        <input type="text" required value={vencimentos} onChange={e => setVencimentos(e.target.value)} placeholder="Ex: 10/05/2026 e 10/06/2026" className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Valor Total Atualizado (R$) *</label>
                                        <input type="text" required value={valorTotal} onChange={e => setValorTotal(e.target.value)} placeholder="Ex: 1.842,75" className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* Signatário interno (único assinante do documento) */}
                            <SignatariosSelector
                                comProcurador
                                comTestemunhas={false}
                                value={signatarios}
                                onChange={setSignatarios}
                            />

                            {/* Observações internas */}
                            <div>
                                <label className={labelCls}>Observações Internas (não constam no documento)</label>
                                <textarea
                                    value={obs}
                                    onChange={e => setObs(e.target.value)}
                                    rows={3}
                                    placeholder="Ex: 3ª tentativa de cobrança; boleto reemitido em 25/06..."
                                    className={`${inputCls} resize-y`}
                                />
                            </div>

                        </form>
                    )}
                </div>

                {/* Footer */}
                {!successData && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={pedirParaFechar}
                            disabled={loading}
                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="mora-form"
                            disabled={loading}
                            className="px-6 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-70 flex items-center justify-center min-w-[170px]"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Gerar Notificação'}
                        </button>
                    </div>
                )}
            </div>
        </div>

        <ConfirmModal
            isOpen={confirmandoDescarte}
            onClose={continuarPreenchendo}
            onConfirm={descartarEFechar}
            title="Descartar a notificação?"
            message="Você já preencheu dados desta notificação extrajudicial. Se fechar agora, tudo o que foi digitado se perde."
            confirmLabel="Descartar"
            cancelLabel="Continuar preenchendo"
            variant="warning"
        />
        </>,
        document.body
    );
};

export default NotificacaoMoraModal;
